import React, { useState, useEffect } from "react";
import LandingCarousel from "./components/LandingCarousel";
import Benefits from "./components/Benefits";
import Reviews from "./components/Reviews";
import ContactForm from "./components/ContactForm";
import NavigationDrawer from "./components/NavigationDrawer";
import NavigationBar from "./components/NavigationBar";
import MenuButton from "./components/MenuButton";
import GradingJourney from "./components/GradingJourney";
import AboutOurClub from "./components/AboutOurClub";
import ThinkingOfJoining from "./components/ThinkingOfJoining";
import UpcomingEvents from "./components/UpcomingEvents";
import Competitions from "./components/Competitions";
import Safeguarding from "./components/Safeguarding";
import Instructors from "./components/Instructors";
import Theory from "./components/Theory";
import Footer from "./components/Footer";
import TrialClassForm from "./components/TrialClassForm";
import Gallery from "./components/Gallery";
import FeedbackForm from "./components/FeedbackForm";
import AdminLogin from "./components/AdminLogin";
import ResetPassword from "./components/ResetPassword";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

type Page =
  | "home"
  | "grading-journey"
  | "about-our-club"
  | "thinking-of-joining"
  | "upcoming-events"
  | "competitions"
  | "safeguarding"
  | "instructors"
  | "trial-class"
  | "gallery"
  | "feedback"
  | "theory";

const VALID_PAGES = new Set<Page>([
  "home",
  "grading-journey",
  "about-our-club",
  "thinking-of-joining",
  "upcoming-events",
  "competitions",
  "safeguarding",
  "instructors",
  "trial-class",
  "gallery",
  "feedback",
  "theory",
]);

function pageFromPath(pathname: string): Page {
  const slug = pathname.replace(/^\//, "") || "home";
  return VALID_PAGES.has(slug as Page) ? (slug as Page) : "home";
}

function AppInner(): JSX.Element {
  const { user, signOut, isRecovery } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<Page>(() =>
    pageFromPath(window.location.pathname),
  );
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      setCurrentPage(e.state?.page ?? pageFromPath(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleToggleDrawer = (): void => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleCloseDrawer = (): void => {
    setIsDrawerOpen(false);
  };

  const handleNavigate = (page: string): void => {
    const next = page as Page;
    const path = next === "home" ? "/" : `/${next}`;
    window.history.pushState({ page: next }, "", path);
    setCurrentPage(next);
  };

  const renderPage = (): JSX.Element => {
    switch (currentPage) {
      case "gallery":
        return <Gallery />;
      case "feedback":
        return <FeedbackForm />;
      case "trial-class":
        return <TrialClassForm />;
      case "grading-journey":
        return <GradingJourney />;
      case "about-our-club":
        return <AboutOurClub onNavigate={handleNavigate} />;
      case "thinking-of-joining":
        return <ThinkingOfJoining onNavigate={handleNavigate} />;
      case "upcoming-events":
        return <UpcomingEvents />;
      case "competitions":
        return <Competitions />;
      case "safeguarding":
        return <Safeguarding />;
      case "instructors":
        return <Instructors />;
      case "theory":
        return <Theory />;
      case "home":
      default:
        return (
          <>
            <LandingCarousel onNavigate={handleNavigate} />
            <Benefits onNavigate={handleNavigate} />
            <Reviews onNavigate={handleNavigate} />
            <ContactForm />
          </>
        );
    }
  };

  return (
    <div className="App">
      {/* Desktop Navigation Bar */}
      <NavigationBar onNavigate={handleNavigate} currentPage={currentPage} />

      {/* Mobile Burger Menu and Drawer */}
      <MenuButton onClick={handleToggleDrawer} isOpen={isDrawerOpen} />
      <NavigationDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onNavigate={handleNavigate}
      />
      {renderPage()}
      <Footer
        user={user}
        onAdminLogin={() => setShowLoginModal(true)}
        onAdminLogout={signOut}
      />

      {showLoginModal && (
        <AdminLogin onClose={() => setShowLoginModal(false)} />
      )}

      {isRecovery && <ResetPassword />}
    </div>
  );
}

function App(): JSX.Element {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
